import React, { useState, useEffect, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import Chatbot from './components/Chatbot';
import ExitIntentPopup from './components/ExitIntentPopup';
import GraziePage from './pages/GraziePage';
import SocialProof from './components/SocialProof';

import HomePage from './pages/HomePage';
import CatalogoIncentivi from './pages/CatalogoIncentivi';
import ComeFunziona from './pages/ComeFunziona';
import CessioneCrediti from './pages/CessioneCrediti';
import GestioneBandi from './pages/GestioneBandi';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import ProfilePage from './pages/ProfilePage';
import ReportPage from './pages/ReportPage';
import ReportLanding from './pages/ReportLanding';
import AdminPanel from './pages/AdminPanel';
import AdminAnalytics from './pages/AdminAnalytics';
import FAQPage from './pages/FAQPage';
import TerminiCondizioni from './pages/TerminiCondizioni';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Disclaimer from './pages/Disclaimer';

import './styles/App.css';

export const AuthContext = createContext();

const API = axios.create({ baseURL: '/api' });

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('vi_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('vi_token');
    if (token) {
      API.get('/auth/me')
        .then(res => setUser(res.data.user))
        .catch(() => { localStorage.removeItem('vi_token'); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('vi_token', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('vi_token');
    setUser(null);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f0f4ff' }}>
      <div className="spinner"></div>
    </div>
  );

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, API }}>
      <Router>
        <Toaster position="top-right" />
        <ScrollToTop />
        <Navbar />
        <main style={{ minHeight: 'calc(100vh - 160px)' }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/incentivi" element={<CatalogoIncentivi />} />
            <Route path="/come-funziona" element={<ComeFunziona />} />
            <Route path="/cessione-crediti" element={<CessioneCrediti />} />
            <Route path="/gestione-bandi" element={<GestioneBandi />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/dashboard" />} />
            <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/dashboard" />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" />} />
            <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/login" />} />
            <Route path="/report" element={user ? <ReportPage /> : <Navigate to="/login" />} />
            <Route path="/report-incentivi" element={<ReportLanding />} />
            <Route path="/admin" element={user?.ruolo === 'admin' ? <AdminPanel /> : <Navigate to="/" />} />
            <Route path="/admin/analytics" element={user?.ruolo === 'admin' ? <AdminAnalytics /> : <Navigate to="/" />} />
            <Route path="/termini-e-condizioni" element={<TerminiCondizioni />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/grazie" element={<GraziePage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppButton />
        <Chatbot />
        <ExitIntentPopup />
        <SocialProof />
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
